import { z } from "zod";
import { Profile } from "./profile.type";

// ── Enums ─────────────────────────────────────────────────────────────────────

export const TeamMemberRole = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
  VIEWER: "viewer",
  CONTRIBUTOR: "contributor",
} as const;

export type TeamMemberRole = (typeof TeamMemberRole)[keyof typeof TeamMemberRole];

// ── TeamMember ────────────────────────────────────────────────────────────────

export const TeamMember = z.object({
  id: z.string().describe("Unique resource identifier"),
  team_id: z.string().describe("Owning team ID"),
  user_id: z.string().describe("User ID"),
  role: z
    .enum(Object.values(TeamMemberRole) as [string, ...string[]])
    .describe("Member role in the team"),
  is_default: z
    .boolean()
    .optional()
    .describe(
      "True when this is the user's personal default team (created at signup). At most one per user.",
    ),
  // ── Computed embed field — present only when ?embed=profile is requested ──
  profile: Profile.partial()
    .optional()
    .describe("Embedded profile — present only when ?embed=profile"),
  created_at: z.coerce.date().describe("Creation timestamp"),
  updated_at: z.coerce.date().describe("Last update timestamp"),
});
export type TeamMember = z.infer<typeof TeamMember>;

// ── Input DTOs — derived from TeamMember to avoid field duplication ────────────

// Create — team_id injected from URL; id/created_at/updated_at auto-generated; profile is embed-only
export const TeamMemberCreate = TeamMember.omit({
  id: true,
  team_id: true,
  created_at: true,
  updated_at: true,
  profile: true,
}).extend({
  role: z.enum(Object.values(TeamMemberRole) as [string, ...string[]]).default("member"),
});
export type TeamMemberCreate = z.infer<typeof TeamMemberCreate>;

// Update — user_id is immutable; all other fields optional
export const TeamMemberUpdate = TeamMemberCreate.omit({
  user_id: true,
}).partial();
export type TeamMemberUpdate = z.infer<typeof TeamMemberUpdate>;
